import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Loader2, Copy, Check, Plus, Trash2, ArrowLeft, AlertTriangle } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import api from '../lib/api';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from '../i18n';

const STEPS = ['Describe', 'Review', 'Send', 'Done'];

const QUICK_STARTS = [
  { label: 'Car Sale', prompt: "I'm selling a vehicle to someone. Include price, payment terms, vehicle details, and transfer of title." },
  { label: 'Freelance Work', prompt: "I'm hiring a freelancer for a project. Include scope of work, payment terms, timeline, and ownership of deliverables." },
  { label: 'Personal Loan', prompt: "I'm lending money to someone. Include loan amount, interest rate, repayment schedule, and what happens if they miss payments." },
  { label: 'Roommate Agreement', prompt: "I'm entering a roommate arrangement. Include rent split, utilities, house rules, and move-out terms." },
  { label: 'NDA', prompt: "I need a non-disclosure agreement. Include what's confidential, duration, and consequences of breach." },
];

export default function QuickContractPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [step, setStep] = useState(0);
  const [description, setDescription] = useState('');
  const [contractText, setContractText] = useState('');
  const [dealId, setDealId] = useState(null);
  const [contractId, setContractId] = useState(null);
  const [signers, setSigners] = useState([{ name: '', email: '' }]);
  const [signingResults, setSigningResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [copiedIndex, setCopiedIndex] = useState(null);
  const [usage, setUsage] = useState(null);
  const [showPlatformAgreement, setShowPlatformAgreement] = useState(false);
  const [platformAgreed, setPlatformAgreed] = useState(false);
  const [checkingLegal, setCheckingLegal] = useState(true);

  useEffect(() => {
    api.get("/subscriptions/usage").then(data => setUsage(data)).catch(() => {});
  }, []);

  // Check if user has accepted platform agreement
  useEffect(() => {
    api.get("/legal/status")
      .then(data => {
        if (!data.has_platform_agreement) {
          setShowPlatformAgreement(true);
        }
        setCheckingLegal(false);
      })
      .catch(() => setCheckingLegal(false));
  }, []);

  const handleAcceptPlatformAgreement = async () => {
    try {
      await api.post("/legal/accept", { type: "platform_agreement" });
      setShowPlatformAgreement(false);
      setPlatformAgreed(true);
      toast.success("Agreement accepted");
    } catch (err) {
      toast.error("Failed to record agreement");
    }
  };

  const handleGenerate = async () => {
    if (!description.trim()) {
      toast.error('Please describe your contract first.');
      return;
    }
    setLoading(true);
    try {
      const dealRes = await api.post('/deals', {
        title: description.slice(0, 80) + (description.length > 80 ? '...' : ''),
        type: 'quick_contract',
        summary: description,
      });
      const newDealId = dealRes._id || dealRes.id;
      setDealId(newDealId);

      const contractRes = await api.post('/deals/' + newDealId + '/contracts/generate', {
        contract_type: 'general_agreement',
        languages: ['en'],
        special_terms: description,
        force_generate: true,
      });
      const content = contractRes.content;
      if (content && typeof content === 'object') {
        const lang = Object.keys(content)[0];
        setContractText(lang ? content[lang] : '');
      } else if (typeof content === 'string') {
        setContractText(content);
      } else {
        setContractText('');
      }
      setContractId(contractRes._id || null);
      setStep(1);
    } catch (err) {
      console.error(err);
      toast.error('Failed to generate contract. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleRegenerate = async () => {
    setLoading(true);
    try {
      const contractRes = await api.post('/deals/' + dealId + '/contracts/generate', {
        contract_type: 'general_agreement',
        languages: ['en'],
        special_terms: description,
        force_generate: true,
      });
      const content = contractRes.content;
      if (content && typeof content === 'object') {
        const lang = Object.keys(content)[0];
        setContractText(lang ? content[lang] : '');
      } else if (typeof content === 'string') {
        setContractText(content);
      } else {
        setContractText('');
      }
      setContractId(contractRes._id || null);
    } catch (err) {
      toast.error('Failed to regenerate. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async () => {
    const validSigners = signers.filter(s => s.name.trim());
    if (validSigners.length === 0) {
      toast.error('Please add at least one signer.');
      return;
    }
    setLoading(true);
    try {
      const res = await api.post('/guest/send-for-signing', {
        contract_id: contractId,
        deal_id: dealId,
        recipients: validSigners.map(s => ({
          name: s.name.trim(),
          email: s.email.trim() || null,
        })),
        current_user_id: user?._id || user?.id || '',
      });
      const links = res.signing_links || [];
      setSigningResults(links.map(l => ({
        name: l.name,
        email: l.email || null,
        link: window.location.origin + l.url,
      })));
      setStep(3);
    } catch (err) {
      console.error(err);
      toast.error('Failed to send for signing. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const addSigner = () => {
    setSigners([...signers, { name: '', email: '' }]);
  };

  const removeSigner = (index) => {
    if (signers.length <= 1) return;
    setSigners(signers.filter((_, i) => i !== index));
  };

  const updateSigner = (index, field, value) => {
    const updated = [...signers];
    updated[index] = { ...updated[index], [field]: value };
    setSigners(updated);
  };

  const copyLink = (link, index) => {
    navigator.clipboard.writeText(link);
    setCopiedIndex(index);
    toast.success('Link copied to clipboard');
    setTimeout(() => setCopiedIndex(null), 2000);
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-2xl mx-auto px-4 py-8">
        <div className="mb-8">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-4"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </button>
          <h1 className="text-2xl font-semibold text-foreground">New Contract</h1>
          {usage && usage?.contracts?.limit > 0 && (
            <p className={`text-sm mt-1 ${usage?.contracts?.used >= usage?.contracts?.limit ? "text-red-600 font-medium" : "text-gray-500"}`}>
              {usage?.contracts?.used} of {usage?.contracts?.limit} AI contracts used this month
            </p>
          )}
        </div>

        <div className="flex items-center gap-2 mb-8">
          {STEPS.map((label, i) => (
            <React.Fragment key={label}>
              <div className="flex items-center gap-2">
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                    i <= step ? 'bg-foreground text-background' : 'bg-muted text-muted-foreground'
                  }`}
                >
                  {i + 1}
                </div>
                <span className={`text-sm hidden sm:inline ${
                  i <= step ? 'text-foreground font-medium' : 'text-muted-foreground/60'
                }`}>
                  {label}
                </span>
              </div>
              {i < STEPS.length - 1 && (
                <div className={`flex-1 h-px ${i < step ? 'bg-foreground' : 'bg-muted'}`} />
              )}
            </React.Fragment>
          ))}
        </div>

        {step === 0 && (
          <Card>
            <CardContent className="pt-6">
              <Label className="text-base font-medium text-foreground mb-2 block">
                Describe your contract in plain English
              </Label>
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Example: I'm selling my 2019 Honda Civic to John Smith for $15,000. He'll pay $5,000 upfront and $10,000 within 30 days."
                className="min-h-[160px] text-base mb-4"
              />
              <div className="mb-6">
                <p className="text-sm text-muted-foreground mb-2">Quick start:</p>
                <div className="flex flex-wrap gap-2">
                  {QUICK_STARTS.map((qs) => (
                    <button
                      key={qs.label}
                      onClick={() => setDescription(qs.prompt)}
                      className="px-3 py-1.5 text-sm rounded-full border border-border text-muted-foreground hover:bg-muted hover:border-foreground/30 transition-colors"
                    >
                      {qs.label}
                    </button>
                  ))}
                </div>
              </div>
              <Button onClick={handleGenerate} disabled={loading || !description.trim()} className="w-full">
                {loading ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Generating...</>
                ) : (
                  'Generate Contract'
                )}
              </Button>
            </CardContent>
          </Card>
        )}

        {step === 1 && (
          <Card>
            <CardContent className="pt-6">
              <Label className="text-base font-medium text-foreground mb-2 block">
                Review your contract
              </Label>
              <p className="text-sm text-muted-foreground mb-4">
                Edit anything that needs changing, or regenerate if you want a fresh version.
              </p>
              <Textarea
                value={contractText}
                onChange={(e) => setContractText(e.target.value)}
                className="min-h-[320px] text-sm font-mono mb-4"
              />
              <div className="flex gap-3">
                <Button variant="outline" onClick={handleRegenerate} disabled={loading}>
                  {loading ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Regenerating...</>
                  ) : (
                    'Regenerate'
                  )}
                </Button>
                <Button onClick={() => setStep(2)} disabled={loading || !contractText.trim()} className="flex-1">
                  Looks Good
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {step === 2 && (
          <Card>
            <CardContent className="pt-6">
              <Label className="text-base font-medium text-foreground mb-1 block">
                Who needs to sign this?
              </Label>
              <p className="text-sm text-muted-foreground mb-6">
                Add the people who need to sign. Email is optional — without it, you will get a shareable link instead.
              </p>
              <div className="space-y-4 mb-6">
                {signers.map((signer, i) => (
                  <div key={i} className="flex gap-3 items-start">
                    <div className="flex-1 space-y-2">
                      <Input placeholder="Name" value={signer.name} onChange={(e) => updateSigner(i, 'name', e.target.value)} />
                      <Input placeholder="Email (optional)" type="email" value={signer.email} onChange={(e) => updateSigner(i, 'email', e.target.value)} />
                    </div>
                    {signers.length > 1 && (
                      <button onClick={() => removeSigner(i)} className="mt-2 text-muted-foreground hover:text-red-500">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
              <button onClick={addSigner} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-6">
                <Plus className="h-4 w-4" />
                Add another signer
              </button>
              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setStep(1)}>Back</Button>
                <Button onClick={handleSend} disabled={loading || !signers.some(s => s.name.trim())} className="flex-1">
                  {loading ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Sending...</>
                  ) : (
                    'Send for Signing'
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {step === 3 && (
          <Card>
            <CardContent className="pt-6">
              <h2 className="text-lg font-semibold text-foreground mb-2">Contract sent</h2>
              <p className="text-sm text-muted-foreground mb-6">
                Your contract is ready for signing. Share the links below with your recipients.
              </p>
              <div className="space-y-4 mb-8">
                {signingResults.map((result, i) => (
                  <div key={i} className="p-4 bg-muted rounded-lg border">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium text-foreground">{result.name}</span>
                      {result.email ? (
                        <span className="text-sm text-muted-foreground">Notified by email</span>
                      ) : (
                        <span className="text-sm text-muted-foreground">Share link manually</span>
                      )}
                    </div>
                    {result.link && (
                      <div className="flex items-center gap-2">
                        <code className="flex-1 text-xs bg-background border rounded px-3 py-2 text-gray-600 truncate">
                          {result.link}
                        </code>
                        <Button variant="outline" size="sm" onClick={() => copyLink(result.link, i)}>
                          {copiedIndex === i ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                        </Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
              <Button variant="outline" onClick={() => navigate('/deals')} className="w-full">
                View My Contracts
              </Button>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Platform Agreement Modal */}
      <Dialog open={showPlatformAgreement} onOpenChange={() => {}}>
        <DialogContent className="sm:max-w-lg" onPointerDownOutside={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              AI-Generated Contract Disclaimer
            </DialogTitle>
            <DialogDescription>
              Please review and acknowledge before creating contracts
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="p-4 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 text-sm space-y-3">
              <p>Contracts generated by PocketFiler are created using <strong>artificial intelligence</strong>. They are provided for convenience and <strong>do NOT constitute legal advice</strong>.</p>
              <p>Before executing any AI-generated contract, you should:</p>
              <ul className="list-disc pl-5 space-y-1">
                <li>Review the entire document carefully</li>
                <li>Verify all terms match your intentions</li>
                <li>Consult a qualified attorney for important agreements</li>
              </ul>
              <p>PocketFiler and Lucid Tech LLC are not law firms and do not provide legal representation.</p>
            </div>

            <div className="flex items-start gap-2">
              <input
                type="checkbox"
                id="platformAgree"
                checked={platformAgreed}
                onChange={(e) => setPlatformAgreed(e.target.checked)}
                className="mt-1 h-4 w-4 rounded border-gray-300"
              />
              <label htmlFor="platformAgree" className="text-sm">
                I understand that AI-generated contracts are not legal advice and that I am responsible for reviewing all content before use
              </label>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => navigate('/dashboard')}
            >
              Go Back
            </Button>
            <Button
              onClick={handleAcceptPlatformAgreement}
              disabled={!platformAgreed}
            >
              I Understand — Continue
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
